import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    TemplateRef
} from '@angular/core';
import * as _ from 'lodash-es';
import {nanoid} from 'nanoid';
import {Observable, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {TypedForm} from '../../../common/form-types';
import {getFormControlsValuesWithUpdatesStream, setControlEnabledState} from '../../../common/form-utils';
import {createNumberFormat} from '../../../common/formatters';
import {TypedSimpleChanges} from '../../../common/typed-on-changes';
import {destroy$} from '../../../components/destroyable';
import {
    getOrderQtyDecimalsInputParams,
    getOrderQtyMultiCurrencyInfo,
    ORDER_QTY_ASSET_TYPE_LABEL,
    OrderQtyDecimalsInputParams,
    OrderQtyMultiCurrencyInfo,
    OrderQtyMultiCurrencyService
} from '../../../components/order-qty-multi-currency';
import {isAsecurity} from '../../../components/security/aspreader-security';
import {SelectItem} from '../../../components/select-input/select-item';
import {SwitchState} from '../../../components/switch';
import {
    createDisplayDecimal,
    DisplayDecimal,
    formatQuantity,
    SymbolDecimalsInputParams,
    SymbolDecimalsService,
    SymbolFormats
} from '../../../components/symbol-decimals';
import {AssetType, getOppositeAssetType} from '../../../enums/asset-type';
import {isExchangeWithSpecificSizeUnit} from '../../../enums/exchange';
import {
    OrderAlgoType,
    SNIPER_DELAY_MAX_MS,
    SNIPER_DELAY_MIN_MS,
    SNIPER_DELAY_UNIT_MENU,
    TWAP_STEP_DELAY_MAX_SEC,
    TWAP_STEP_DELAY_MIN_SEC,
    TWAP_STEP_DELAY_UNIT_MENU
} from '../../../enums/order-algo-type';
import {OrderTransactionType} from '../../../enums/order-transaction-type';
import {ORDER_TYPE_LABEL_DICT, OrdType} from '../../../enums/order-type';
import {Side, SIDE_CLASSNAME_DICT, SIDE_LABEL_DICT} from '../../../enums/side';
import {TIME_IN_FORCE_SWITCH_CONFIG} from '../../../enums/time-in-force';
import {Security} from '../../../models/security';
import {OrderFormValue} from '../../components/float-widget-common/order-modal-form-block';

const ORDER_TYPE_SELECT_ITEMS: Array<SelectItem<OrdType>> = [
    OrdType.LIMIT,
    OrdType.MARKET,
    OrdType.STOP,
    OrdType.STOP_LIMIT
].map(ordType => ({
    value: ordType,
    label: ORDER_TYPE_LABEL_DICT[ordType] || ordType
}));

const SIDE_SWITCH_CONFIG: Array<SwitchState<Side>> = [
    Side.BUY,
    Side.SELL
].map(side => ({
    label:         SIDE_LABEL_DICT[side],
    value:         side,
    activeClasses: SIDE_CLASSNAME_DICT[side]
}));

@Component({
    selector: 'order-form-block',
    templateUrl: './order-form-block.component.html',
    styleUrls: ['./order-form-block.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OrderFormBlockComponent implements OnInit, OnChanges {

    @Input()
    public orderForm: TypedForm<OrderFormValue>;

    @Input()
    public orderLeavesQty: number;

    @Input()
    public security: Security;

    @Input()
    public isActionsDisabled: boolean;

    @Input()
    public isReplaceDisabled: boolean;

    @Input()
    public isActionsUnavailable: boolean;

    @Input()
    public isOrderInited$: Observable<boolean>;

    @Input()
    public maxUnfilledTpl: TemplateRef<unknown>;

    @Input()
    public maxShowTpl: TemplateRef<unknown>;

    @Output()
    public onSubmit: EventEmitter<OrderTransactionType> = new EventEmitter<OrderTransactionType>();

    public readonly destroy$ = destroy$();

    public readonly orderTypeMenuItems: SelectItem[] = ORDER_TYPE_SELECT_ITEMS;

    public readonly SIDE_SWITCH_CONFIG = SIDE_SWITCH_CONFIG;
    public readonly TIME_IN_FORCE_SWITCH_CONFIG = TIME_IN_FORCE_SWITCH_CONFIG;
    public readonly ORDER_TYPE = OrdType;
    public readonly ORDER_QTY_ASSET_TYPE_LABEL = ORDER_QTY_ASSET_TYPE_LABEL;
    public readonly OrderTransactionType = OrderTransactionType;
    public readonly OrderAlgoType = OrderAlgoType;
    public readonly TWAP_STEP_DELAY_UNIT_MENU = TWAP_STEP_DELAY_UNIT_MENU;
    public readonly TWAP_STEP_DELAY_MIN_SEC = TWAP_STEP_DELAY_MIN_SEC;
    public readonly TWAP_STEP_DELAY_MAX_SEC = TWAP_STEP_DELAY_MAX_SEC;
    public readonly SNIPER_DELAY_UNIT_MENU = SNIPER_DELAY_UNIT_MENU;
    public readonly SNIPER_DELAY_MIN_MS = SNIPER_DELAY_MIN_MS;
    public readonly SNIPER_DELAY_MAX_MS = SNIPER_DELAY_MAX_MS;

    public get twapStepDelayUnitTextSuffix(): string {
        const unitItem = this.orderForm
            && this.orderForm.controls.twapStepDelayUnit.value
            && TWAP_STEP_DELAY_UNIT_MENU.find(item => item.value === this.orderForm.controls.twapStepDelayUnit.value);

        return unitItem && unitItem.label || '';
    }

    public get sniperDelayUnitTextSuffix(): string {
        const unitItem = this.orderForm
            && this.orderForm.controls.sniperDelayUnit.value
            && SNIPER_DELAY_UNIT_MENU.find(item => item.value === this.orderForm.controls.sniperDelayUnit.value);

        return unitItem && unitItem.label || '';
    }

    public symbolDecimalsInputParams: SymbolDecimalsInputParams;

    public isExchangeWithSpecificSizeUnit: boolean = false;

    public orderQtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo;

    public qtyInputParams: OrderQtyDecimalsInputParams;

    public oppositeCurrencyQtyFormatter: (value: number) => DisplayDecimal;

    public qtyOppositeCurrencySymbol: string;

    public readonly id = nanoid();

    public lastOppositeCurrencyQty: number;

    private oppositeCurrencyQtySubscription: Subscription;

    private securityFormats: SymbolFormats;

    constructor(
        private symbolDecimalsService: SymbolDecimalsService,
        private orderQtyMultiCurrencyService: OrderQtyMultiCurrencyService
    ) {}

    public ngOnChanges(changes: TypedSimpleChanges<OrderFormBlockComponent>): void {
        if (changes.security) {
            this.updateBySecurity();
        }
    }

    public ngOnInit(): void {
        this.updateBySecurity();

        this.isOrderInited$.pipe(
            filter(isInited => isInited),
            take(1),
            tap(() => {
                this.updateBySecurity();
                this.initForm();
            })
        ).subscribe();
    }

    public goToConfirmation(transactionType: OrderTransactionType): void {
        this.onSubmit.emit(transactionType);
    }

    private initForm(): void {
        // this.listenToQtyAssetTypeControl(); // TODO: Check, when support Replace

        this.updateQtyInputParams();
        this.updateOppositeCurrencyQtyStream();
    }

    private updateBySecurity(): void {
        const security = this.security;

        this.symbolDecimalsInputParams = this.symbolDecimalsService.getSecurityInputParams(security);
        this.securityFormats = this.symbolDecimalsService.getSecurityFormats(security);

        /**
         * Don't show Total value calculated as size * price for the cases where this math in wrong.
         */
        this.isExchangeWithSpecificSizeUnit = isExchangeWithSpecificSizeUnit(
            security && security.securityExchange
        );

        const controls = this.orderForm && this.orderForm.controls;
        this.orderQtyMultiCurrencyInfo = security && getOrderQtyMultiCurrencyInfo(security, controls && {
            orderType:   controls.orderType.value,
            side:        controls.side.value,
            accountType: controls.accountBase.value && controls.accountBase.value.type
        });
        this.updateQtyAssetTypeControlBySecurity();
        this.updateQtyInputParams();
        // this.updateOppositeCurrencyQtyStream(); // TODO: Check, when support Replace
    }

    private listenToQtyAssetTypeControl(): void {
        const control = this.orderForm.controls.qtyAssetType;

        control.valueChanges.subscribe(() => {
            this.updateQtyInputParams();
            this.setQtyControlToLastOpposite();
        });
    }

    private updateQtyAssetTypeControlBySecurity(): void {
        if (this.orderForm) {
            const control = this.orderForm.controls.qtyAssetType;

            const isEnabled = !this.isReplaceDisabled
                && this.orderQtyMultiCurrencyInfo
                && this.orderQtyMultiCurrencyInfo.isMultiCurrencyAllowed;

            setControlEnabledState(control, isEnabled);
        }
    }

    private updateQtyInputParams(): void {
        if (this.orderForm && this.orderQtyMultiCurrencyInfo && this.symbolDecimalsInputParams) {
            const qtyAssetType: AssetType = this.orderForm.controls.qtyAssetType.value;
            const isQtyInBaseCurrency = !this.orderQtyMultiCurrencyInfo.isMultiCurrencyAllowed
                || qtyAssetType === AssetType.BASE;

            const qtyInputAssetType = isQtyInBaseCurrency
                ? AssetType.BASE
                : AssetType.QUOTE;

            this.qtyInputParams = getOrderQtyDecimalsInputParams({
                securityInputParams:  this.symbolDecimalsInputParams,
                qtyAssetType:         qtyInputAssetType,
                qtyMultiCurrencyInfo: this.orderQtyMultiCurrencyInfo
            });

            this.updateOppositeCurrencyQtyFormatter(qtyInputAssetType);

            this.qtyOppositeCurrencySymbol = isAsecurity(this.security)
                ? ''
                : !isQtyInBaseCurrency
                    ? this.security.baseAsset.symbol
                    : this.security.quoteAsset.symbol;
        }
    }

    private updateOppositeCurrencyQtyFormatter(qtyInputAssetType: AssetType): void {
        if (this.orderQtyMultiCurrencyInfo && this.symbolDecimalsInputParams && this.securityFormats) {
            const oppositeQtyAssetType = getOppositeAssetType(qtyInputAssetType);

            const {baseQtyAssetType} = this.orderQtyMultiCurrencyInfo;

            const isNormalQtyFormatting = oppositeQtyAssetType === baseQtyAssetType;

            let formatter: (value: number) => DisplayDecimal;

            if (isNormalQtyFormatting) {
                formatter = value => formatQuantity(value, this.securityFormats);
            } else {
                const oppositeQtyInputParams = getOrderQtyDecimalsInputParams({
                    qtyMultiCurrencyInfo: this.orderQtyMultiCurrencyInfo,
                    securityInputParams:  this.symbolDecimalsInputParams,
                    qtyAssetType:         oppositeQtyAssetType
                });
                const numberFormat = createNumberFormat({
                    isDecimalRequired: true,
                    decimalCount:      oppositeQtyInputParams.decimalCount,
                    precision:         oppositeQtyInputParams.precision
                });

                formatter = value => createDisplayDecimal(value, numberFormat);
            }

            this.oppositeCurrencyQtyFormatter = formatter;
        }
    }

    private updateOppositeCurrencyQtyStream(): void {
        if (this.orderForm && this.security) {
            this.lastOppositeCurrencyQty = undefined;

            const oppositeCurrencyQty$ = getFormControlsValuesWithUpdatesStream<OrderFormValue>(this.orderForm, [
                'accountBase',
                'side',
                'limitPrice',
                'quantity',
                'qtyAssetType',
                'orderType'
            ]).pipe(
                distinctUntilChanged<Partial<OrderFormValue>>(_.isEqual),
                switchMap(values =>
                    this.orderQtyMultiCurrencyService.getOppositeCurrencyQtyStream({
                        security:     this.security,
                        side:         values.side,
                        limitPrice:   values.limitPrice,
                        qtyValue:     values.quantity,
                        qtyAssetType: values.qtyAssetType,
                        orderType:    values.orderType,
                        accountType:  values.accountBase && values.accountBase.type
                    })
                ),
                tap(qty => this.lastOppositeCurrencyQty = qty),
                takeUntil(this.destroy$)
            );

            if (this.oppositeCurrencyQtySubscription) {
                this.oppositeCurrencyQtySubscription.unsubscribe();
            }

            this.oppositeCurrencyQtySubscription = oppositeCurrencyQty$.subscribe();
        }
    }

    private setQtyControlToLastOpposite(): void {
        if (this.orderForm && this.lastOppositeCurrencyQty !== undefined) {
            const newQty = this.lastOppositeCurrencyQty;

            this.orderForm.controls.quantity.setValue(newQty);
        }
    }

}
